/**
 * How this ticket is doing against its SLA target.
 *
 * Rendered twice: as a compact cell in the ticket list, and prominently in the
 * detail header's status strip. Both read the same assessment so a ticket can
 * never look "on track" in the list and "past target" on its own page.
 */

import { CircleCheck, Clock, Icon, TriangleAlert } from "@norican/icons";
import { Badge, Typography } from "@norican/react";

import { RelativeTime, useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";

import type { TicketSummary } from "../api/contract";
import { assessSla, type SlaAssessment } from "../lib/ticket-display";

/** The translated one-liner for an assessment. */
function useSlaLabel() {
  const { t } = useT("tickets");
  const { formatDateTime } = useDateFormatter();

  return (assessment: SlaAssessment): string => {
    switch (assessment.state) {
      case "BREACHED":
        return t("sla.breached");
      case "DUE_SOON":
      case "ON_TRACK":
        return t("sla.dueAt", { date: formatDateTime(assessment.target) });
      case "DONE":
        return t("sla.done");
      case "NONE":
        return t("sla.none");
    }
  };
}

export interface SlaCellProps {
  ticket: Pick<TicketSummary, "target_at" | "status"> & { sla_breached?: boolean };
}

/**
 * The list-grid rendering.
 *
 * Only the two states the reader can still do something about take colour — due
 * soon and past target. "On track", "finished" and "no target" are the common
 * cases and stay as quiet muted text, so a column of forty rows reads as a
 * handful of coloured exceptions rather than a wall of badges.
 */
export function SlaCell({ ticket }: SlaCellProps) {
  const { t } = useT("tickets");
  const assessment = assessSla(ticket);

  if (assessment.state === "NONE" || assessment.target === null) {
    return (
      <Typography variant="body-sm" tone="muted">
        {t("sla.none")}
      </Typography>
    );
  }

  if (assessment.state === "BREACHED") {
    return (
      <Badge
        tone="danger"
        variant="solid"
        size="sm"
        className="inline-flex items-center gap-1 whitespace-nowrap"
      >
        <Icon icon={TriangleAlert} size="xs" />
        {t("sla.breached")}
      </Badge>
    );
  }

  if (assessment.state === "DONE") {
    return (
      <Typography
        as="span"
        variant="body-sm"
        tone="muted"
        className="inline-flex items-center gap-1.5"
      >
        <Icon icon={CircleCheck} size="xs" />
        {t("sla.done")}
      </Typography>
    );
  }

  if (assessment.state === "DUE_SOON") {
    return (
      <Badge
        tone={assessment.badgeTone}
        variant="subtle"
        size="sm"
        className="inline-flex items-center gap-1 whitespace-nowrap"
      >
        <Icon icon={Clock} size="xs" />
        <RelativeTime value={assessment.target} mode="relative" />
      </Badge>
    );
  }

  return (
    <Typography
      as="span"
      variant="body-sm"
      tone="muted"
      className="inline-flex items-center gap-1.5 whitespace-nowrap"
    >
      <Icon icon={Clock} size="xs" />
      <RelativeTime value={assessment.target} mode="relative" />
    </Typography>
  );
}

export interface SlaSummaryProps {
  ticket: Pick<TicketSummary, "target_at" | "status"> & { sla_breached?: boolean };
}

/** The detail-page rendering: the state spelled out, with the exact target. */
export function SlaSummary({ ticket }: SlaSummaryProps) {
  const assessment = assessSla(ticket);
  const label = useSlaLabel();
  return <span>{label(assessment)}</span>;
}
